import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { 
  ChartBarIcon, 
  FolderIcon, 
  CubeIcon, 
  BuildingStorefrontIcon,
  ArrowRightOnRectangleIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon,
  EyeIcon,
  CalendarDaysIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import './css/Dashboard.css';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../context/LanguageContext';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    categories: 0,
    products: 0,
    branches: 0,
    heroImages: 0
  });
  const [analytics, setAnalytics] = useState({
    total: 0,
    today: 0,
    week: 0,
    month: 0,
    byDevice: [],
    daily: [],
    topPages: []
  });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, isRTL, toggleLanguage } = useLanguage();

  useEffect(() => {
    fetchStats();
    fetchAnalytics();
  }, [period]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const [categoriesRes, productsRes, branchesRes, heroImagesRes] = await Promise.all([
        axios.get(`${API_URL}/api/categories`),
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/branches`),
        axios.get(`${API_URL}/api/hero-images/all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ data: [] })) // Handle if endpoint doesn't exist yet
      ]);

      setStats({
        categories: categoriesRes.data.length,
        products: productsRes.data.length,
        branches: branchesRes.data.length,
        heroImages: heroImagesRes.data.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/analytics/stats?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
    navigate('/admin/login');
  };

  // Prepare chart data
  const deviceData = {
    labels: analytics.byDevice.map(d => {
      const deviceMap = {
        mobile: t('dashboard.mobile'),
        desktop: t('dashboard.desktop'),
        tablet: t('dashboard.tablet')
      };
      return deviceMap[d.device_type] || d.device_type;
    }),
    datasets: [{
      data: analytics.byDevice.map(d => d.count),
      backgroundColor: [
        'rgba(54, 162, 235, 0.8)',
        'rgba(255, 99, 132, 0.8)',
        'rgba(255, 206, 86, 0.8)'
      ],
      borderColor: [
        'rgba(54, 162, 235, 1)',
        'rgba(255, 99, 132, 1)',
        'rgba(255, 206, 86, 1)'
      ],
      borderWidth: 2
    }]
  };

  const dailyData = {
    labels: analytics.daily.map(d => {
      const date = new Date(d.date);
      return date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }),
    datasets: [{
      label: t('dashboard.views'),
      data: analytics.daily.map(d => d.count),
      borderColor: 'rgba(75, 192, 192, 1)',
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      tension: 0.4,
      fill: true
    }]
  };

  const topPagesData = {
    labels: analytics.topPages.slice(0, 5).map(p => p.page_path || '/'),
    datasets: [{
      label: t('dashboard.views'),
      data: analytics.topPages.slice(0, 5).map(p => p.count),
      backgroundColor: 'rgba(153, 102, 255, 0.8)',
      borderColor: 'rgba(153, 102, 255, 1)',
      borderWidth: 1
    }]
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>{t('dashboard.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`admin-dashboard ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="dashboard-header">
        <div>
          <h1>{t('dashboard.title')}</h1>
          <p className="welcome-text">{t('dashboard.welcome')}</p>
        </div>
        <div className="header-actions">
          <button onClick={toggleLanguage} className="language-toggle-btn">
            {language === 'ar' ? 'EN' : 'AR'}
          </button>
          <button onClick={handleLogout} className="logout-btn">
            <ArrowRightOnRectangleIcon className="icon" />
            {t('dashboard.logout')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card views-card" onClick={() => navigate('/admin/dashboard')}>
          <div className="stat-icon views">
            <EyeIcon />
          </div>
          <div className="stat-info">
            <h3>{analytics.total.toLocaleString()}</h3>
            <p>{t('dashboard.totalViews')}</p>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/admin/categories')}>
          <div className="stat-icon categories">
            <FolderIcon />
          </div>
          <div className="stat-info">
            <h3>{stats.categories}</h3>
            <p>{t('dashboard.categories')}</p>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/admin/products')}>
          <div className="stat-icon products">
            <CubeIcon />
          </div>
          <div className="stat-info">
            <h3>{stats.products}</h3>
            <p>{t('dashboard.products')}</p>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/admin/branches')}>
          <div className="stat-icon branches">
            <BuildingStorefrontIcon />
          </div>
          <div className="stat-info">
            <h3>{stats.branches}</h3>
            <p>{t('dashboard.branches')}</p>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/admin/hero-images')}>
          <div className="stat-icon hero-images">
            <PhotoIcon />
          </div>
          <div className="stat-info">
            <h3>{stats.heroImages || 0}</h3>
            <p>{t('dashboard.heroImages') || 'Hero Images'}</p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats">
        <div className="quick-stat-card">
          <CalendarDaysIcon className="quick-stat-icon" />
          <div>
            <h4>{analytics.today}</h4>
            <p>{t('dashboard.todayViews')}</p>
          </div>
        </div>
        <div className="quick-stat-card">
          <ChartBarIcon className="quick-stat-icon" />
          <div>
            <h4>{analytics.week}</h4>
            <p>{t('dashboard.weekViews')}</p>
          </div>
        </div>
        <div className="quick-stat-card">
          <ChartBarIcon className="quick-stat-icon" />
          <div>
            <h4>{analytics.month}</h4>
            <p>{t('dashboard.monthViews')}</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="charts-header">
          <h2>{t('dashboard.analytics')}</h2>
          <div className="period-selector">
            <label>{t('dashboard.period')}:</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="7">{t('dashboard.last7Days')}</option>
              <option value="30">{t('dashboard.last30Days')}</option>
              <option value="90">{t('dashboard.last90Days')}</option>
            </select>
          </div>
        </div>

        <div className="charts-grid">
          {/* Device Type Chart */}
          <div className="chart-card">
            <h3>{t('dashboard.viewsByDevice')}</h3>
            {analytics.byDevice.length > 0 ? (
              <div className="chart-container">
                <Doughnut 
                  data={deviceData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          padding: 15,
                          font: {
                            size: 12
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <p className="no-data">{t('dashboard.noData')}</p>
            )}
          </div>

          {/* Daily Views Chart */}
          <div className="chart-card">
            <h3>{t('dashboard.dailyViews')}</h3>
            {analytics.daily.length > 0 ? (
              <div className="chart-container">
                <Line 
                  data={dailyData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        display: false
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <p className="no-data">{t('dashboard.noData')}</p>
            )}
          </div>
        </div>

        {/* Top Pages Chart */}
        <div className="chart-card full-width">
          <h3>{t('dashboard.topPages')}</h3>
          {analytics.topPages.length > 0 ? (
            <div className="chart-container">
              <Bar 
                data={topPagesData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: {
                      display: false
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: 1
                      }
                    }
                  }
                }}
              />
            </div>
          ) : (
            <p className="no-data">{t('dashboard.noData')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
